import {
  guidelineInputSchema,
  guidelineListSchema,
  guidelineSchema,
  horizonInputSchema,
  localIsoDay,
  objectiveListSchema,
  OBJECTIVE_RELATION_LABELS,
  PREMISE_CATEGORIES,
  PREMISE_CATEGORY_LABELS,
  premiseInForce,
  premiseInputSchema,
  premiseListSchema,
  premiseSchema,
  perspectiveListSchema,
  perspectiveWeightTotal,
  type Guideline,
  type GuidelineInput,
  type Objective,
  type Premise,
  type PremiseCategoryName,
  type PremiseInput,
  type WorkspaceProject,
} from '@fluxus/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArchiveRestore,
  ArrowDown,
  ArrowUp,
  CalendarRange,
  Compass,
  FolderKanban,
  Lightbulb,
  Lock,
  Pencil,
  Plus,
  Target,
} from 'lucide-react';
import { useId, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Dialog } from '@/components/ui/dialog';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { ProjectPicker, useCurrentProject, workspaceKey } from '@/features/workspace/workspace';
import { errorMessage } from '@/lib/api/errors';
import { useAuth, useCan, useCurrentUser } from '@/lib/auth/session';
import { cn } from '@/lib/cn';
import { apiFieldErrors, hasFieldErrors, zodFieldErrors, type FieldErrors } from '@/lib/forms';

const SERIES = ['bg-series-1', 'bg-series-2', 'bg-series-3', 'bg-series-4', 'bg-series-5', 'bg-series-6'];
const formatWeight = (w: number) => `${w.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;
const formatDay = (d: string) => d.split('-').reverse().join('/');
const textareaClass =
  'w-full resize-y rounded-md border border-border-strong bg-surface-raised px-3 py-2 text-sm shadow-sm focus-visible:border-brand focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-ring/40 aria-invalid:border-status-red-solid';

type Can = (module: 'planejamento-estrategico', action: 'create' | 'edit' | 'delete') => boolean;

/**
 * Estratégia › Planejamento estratégico (T-042, RF-14): horizonte, diretrizes, premissas e o retrato dos
 * objetivos com pesos e relações, num lugar só.
 */
export function PlanejamentoPage() {
  const { organizationId = '' } = useParams();
  const user = useCurrentUser();
  const canDo = useCan();
  const { workspace, projects, project, select } = useCurrentProject(organizationId);
  const readOnly = project?.status !== 'ACTIVE';
  const can: Can = (module, action) => !readOnly && canDo(module, action);

  return (
    <PageContainer className="max-w-5xl">
      <PageHeader
        title="Planejamento estratégico"
        description="O horizonte do plano, as diretrizes que orientam as escolhas, as premissas assumidas e como os objetivos se distribuem e se conectam."
      />
      {workspace.isPending ? (
        <LoadingState className="mt-6" rows={4} label="Carregando o projeto" />
      ) : workspace.isError ? (
        <ErrorState
          className="mt-6"
          error={workspace.error}
          onRetry={() => void workspace.refetch()}
          retrying={workspace.isFetching}
        />
      ) : !project ? (
        <EmptyState
          className="mt-6 rounded-lg border border-dashed border-border py-12"
          icon={FolderKanban}
          title="Nenhum projeto BSC em andamento"
          description="O planejamento pertence a um projeto. Quando a empresa tiver um projeto em andamento, ele aparece aqui."
          action={
            user.organization.isFluxus ? (
              <Button asChild variant="outline">
                <Link to={`/empresas/${organizationId}`}>Abrir um projeto na página da empresa</Link>
              </Button>
            ) : null
          }
        />
      ) : (
        <div key={project.id} className="mt-4 flex flex-col gap-8">
          <ProjectPicker projects={projects} project={project} onSelect={select} />
          {readOnly ? (
            <p className="flex items-center gap-2 rounded-lg border border-border bg-muted px-4 py-3 text-sm">
              <Lock className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              Projeto arquivado: o planejamento fica disponível só para consulta.
            </p>
          ) : null}
          <HorizonSection organizationId={organizationId} project={project} can={can} />
          <GuidelinesSection organizationId={organizationId} projectId={project.id} can={can} />
          <PremisesSection organizationId={organizationId} projectId={project.id} can={can} />
          {canDo('objetivos', 'view') ? (
            <ObjectivesOverview organizationId={organizationId} projectId={project.id} />
          ) : null}
        </div>
      )}
    </PageContainer>
  );
}

function SectionTitle({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof Compass;
  title: string;
  children?: ReactNode;
}) {
  return (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
      <h2 className="flex items-center gap-2 text-base font-bold">
        <Icon className="size-4 text-brand-strong dark:text-brand" aria-hidden />
        {title}
      </h2>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Horizonte

function HorizonSection({
  organizationId,
  project,
  can,
}: {
  organizationId: string;
  project: WorkspaceProject;
  can: Can;
}) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [start, setStart] = useState(String(project.horizonStart));
  const [end, setEnd] = useState(String(project.horizonEnd));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const ids = { start: useId(), end: useId() };
  const years = project.horizonEnd - project.horizonStart + 1;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setFormError(null);
    const parsed = horizonInputSchema.safeParse({ horizonStart: start, horizonEnd: end });
    if (!parsed.success) return setErrors(zodFieldErrors(parsed.error));
    setErrors({});
    setSaving(true);
    try {
      await client.request(`/organizations/${organizationId}/projects/${project.id}/horizon`, {
        method: 'PUT',
        body: parsed.data,
      });
      await queryClient.invalidateQueries({ queryKey: workspaceKey(organizationId) });
      setEditing(false);
      toast.success('Horizonte salvo');
    } catch (err) {
      const fields = apiFieldErrors(err);
      if (hasFieldErrors(fields)) setErrors(fields);
      else setFormError(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section aria-label="Horizonte estratégico" className="rounded-lg border border-border bg-surface p-5">
      <SectionTitle icon={CalendarRange} title="Horizonte estratégico">
        {can('planejamento-estrategico', 'edit') && !editing ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setStart(String(project.horizonStart));
              setEnd(String(project.horizonEnd));
              setErrors({});
              setEditing(true);
            }}
          >
            <Pencil aria-hidden />
            Alterar horizonte
          </Button>
        ) : null}
      </SectionTitle>
      {editing ? (
        <form noValidate onSubmit={submit} className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-4">
            <FormField id={ids.start} label="Ano de início" error={errors.horizonStart}>
              <Input
                {...fieldA11y(ids.start, errors.horizonStart)}
                value={start}
                inputMode="numeric"
                maxLength={4}
                onChange={(e) => setStart(e.target.value)}
                className="h-11 w-28"
              />
            </FormField>
            <FormField id={ids.end} label="Ano de fim" error={errors.horizonEnd}>
              <Input
                {...fieldA11y(ids.end, errors.horizonEnd)}
                value={end}
                inputMode="numeric"
                maxLength={4}
                onChange={(e) => setEnd(e.target.value)}
                className="h-11 w-28"
              />
            </FormField>
          </div>
          {formError ? <FormAlert tone="error">{errorMessage(formError)}</FormAlert> : null}
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button type="button" variant="outline" onClick={() => setEditing(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button type="submit" loading={saving}>
              Salvar horizonte
            </Button>
          </div>
        </form>
      ) : (
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <p className="text-3xl font-bold tabular-nums">
            {project.horizonStart}–{project.horizonEnd}
          </p>
          <p className="text-sm text-muted-foreground">
            {years} {years === 1 ? 'ano' : 'anos'} de planejamento
          </p>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------------------------
// Diretrizes

function GuidelinesSection({
  organizationId,
  projectId,
  can,
}: {
  organizationId: string;
  projectId: string;
  can: Can;
}) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState<Guideline | 'new' | null>(null);
  const [busy, setBusy] = useState(false);
  const [archiving, setArchiving] = useState<Guideline | null>(null);
  const base = `/organizations/${organizationId}/projects/${projectId}/guidelines`;
  const key = ['guidelines', projectId];

  const active = useQuery({
    queryKey: [...key, 'active'],
    queryFn: ({ signal }) => client.request(base, { schema: guidelineListSchema, signal }),
  });
  const archivedQuery = useQuery({
    queryKey: [...key, 'archived'],
    queryFn: ({ signal }) =>
      client.request(base, { query: { archived: 'true' }, schema: guidelineListSchema, signal }),
    enabled: showArchived,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: key });

  const move = useMutation({
    meta: { handlesError: true },
    mutationFn: (ids: string[]) =>
      client.request(`${base}/order`, { method: 'PUT', body: { ids }, schema: guidelineListSchema }),
    onSuccess: () => refresh(),
    onError: async (err) => {
      toast.error('Não foi possível reordenar', { description: errorMessage(err) });
      await refresh();
    },
  });
  const toggleArchive = useMutation({
    meta: { handlesError: true },
    mutationFn: (g: Guideline) =>
      client.request(`${base}/${g.id}/${g.archivedAt ? 'restore' : 'archive'}`, {
        method: 'POST',
        schema: guidelineSchema,
      }),
    onSuccess: async (g) => {
      setArchiving(null);
      toast.success(g.archivedAt ? 'Diretriz arquivada' : 'Diretriz restaurada', { description: g.title });
      await refresh();
    },
    onError: (err) => {
      setArchiving(null);
      toast.error('Não foi possível concluir', { description: errorMessage(err) });
    },
  });

  async function save(input: GuidelineInput) {
    const target = editing === 'new' ? null : editing;
    await client.request(target ? `${base}/${target.id}` : base, {
      method: target ? 'PUT' : 'POST',
      body: input,
      schema: guidelineSchema,
    });
    setEditing(null);
    toast.success(target ? 'Diretriz salva' : 'Diretriz adicionada');
    await refresh();
  }

  const items = active.data?.items ?? [];
  const reorder = (index: number, delta: -1 | 1) => {
    const ids = items.map((g) => g.id);
    const [moved] = ids.splice(index, 1);
    ids.splice(index + delta, 0, moved!);
    queryClient.setQueryData([...key, 'active'], {
      items: ids.map((id, position) => ({ ...items.find((g) => g.id === id)!, position })),
    });
    move.mutate(ids);
  };

  return (
    <section aria-label="Diretrizes">
      <SectionTitle icon={Compass} title="Diretrizes">
        {can('planejamento-estrategico', 'create') ? (
          <Button size="sm" onClick={() => setEditing('new')}>
            <Plus aria-hidden />
            Nova diretriz
          </Button>
        ) : null}
      </SectionTitle>
      <p className="mb-3 max-w-prose text-sm text-muted-foreground">
        Orientações gerais que guiam as escolhas do plano, da mais para a menos prioritária.
      </p>
      {active.isPending ? (
        <LoadingState rows={3} label="Carregando as diretrizes" />
      ) : active.isError ? (
        <ErrorState error={active.error} onRetry={() => void active.refetch()} retrying={active.isFetching} />
      ) : items.length === 0 ? (
        <EmptyState
          className="rounded-lg border border-dashed border-border py-8"
          icon={Compass}
          title="Nenhuma diretriz ainda"
          description="Registre as orientações que a empresa quer seguir, como “crescer com rentabilidade”."
        />
      ) : (
        <ol
          aria-label="Ordem das diretrizes"
          aria-busy={move.isPending || active.isFetching}
          className="flex flex-col gap-2"
        >
          {items.map((g, i) => (
            <li
              key={g.id}
              className="flex flex-col gap-3 rounded-lg border border-border bg-surface p-4 sm:flex-row sm:items-center"
            >
              <div className="flex min-w-0 flex-1 items-start gap-3">
                <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-bold">
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold">{g.title}</p>
                  {g.description ? (
                    <p className="mt-0.5 text-sm text-muted-foreground">{g.description}</p>
                  ) : null}
                </div>
              </div>
              <div className="flex justify-end gap-1">
                {can('planejamento-estrategico', 'edit') ? (
                  <>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Subir ${g.title}`}
                      disabled={i === 0 || move.isPending}
                      onClick={() => reorder(i, -1)}
                    >
                      <ArrowUp aria-hidden />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Descer ${g.title}`}
                      disabled={i === items.length - 1 || move.isPending}
                      onClick={() => reorder(i, 1)}
                    >
                      <ArrowDown aria-hidden />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Editar ${g.title}`}
                      onClick={() => setEditing(g)}
                    >
                      <Pencil aria-hidden />
                    </Button>
                  </>
                ) : null}
                {can('planejamento-estrategico', 'delete') ? (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Arquivar ${g.title}`}
                    onClick={() => setArchiving(g)}
                  >
                    <Archive aria-hidden />
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      )}
      <ArchivedToggle
        checked={showArchived}
        onChange={setShowArchived}
        label="Mostrar diretrizes arquivadas"
      />
      {showArchived ? (
        <ArchivedList
          label="Diretrizes arquivadas"
          query={archivedQuery}
          empty="Nenhuma diretriz arquivada."
          render={(g: Guideline) => g.title}
          canRestore={can('planejamento-estrategico', 'delete')}
          restoring={toggleArchive.isPending}
          onRestore={(g) => toggleArchive.mutate(g)}
        />
      ) : null}

      <Dialog
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        busy={busy}
        title={editing === 'new' ? 'Nova diretriz' : 'Editar diretriz'}
      >
        {editing !== null ? (
          <GuidelineForm
            initial={editing === 'new' ? undefined : editing}
            onSubmit={save}
            onCancel={() => setEditing(null)}
            onBusyChange={setBusy}
          />
        ) : null}
      </Dialog>
      <ConfirmDialog
        open={archiving !== null}
        onOpenChange={(open) => !open && setArchiving(null)}
        title="Arquivar a diretriz?"
        description={`${archiving?.title ?? ''} sai do plano ativo. O histórico fica guardado e você pode restaurá-la depois.`}
        confirmLabel="Arquivar diretriz"
        destructive
        busy={toggleArchive.isPending}
        onConfirm={() => archiving && toggleArchive.mutate(archiving)}
      />
    </section>
  );
}

function GuidelineForm({
  initial,
  onSubmit,
  onCancel,
  onBusyChange,
}: {
  initial?: Guideline;
  onSubmit: (input: GuidelineInput) => Promise<void>;
  onCancel: () => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const ids = { title: useId(), description: useId() };

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setFormError(null);
    const parsed = guidelineInputSchema.safeParse({ title, description });
    if (!parsed.success) return setErrors(zodFieldErrors(parsed.error));
    setErrors({});
    setSaving(true);
    onBusyChange(true);
    try {
      await onSubmit(parsed.data);
    } catch (err) {
      const fields = apiFieldErrors(err);
      if (hasFieldErrors(fields)) setErrors(fields);
      else setFormError(err);
    } finally {
      setSaving(false);
      onBusyChange(false);
    }
  }

  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-5">
      <FormField id={ids.title} label="Diretriz" error={errors.title}>
        <Input
          {...fieldA11y(ids.title, errors.title)}
          value={title}
          maxLength={200}
          placeholder="Ex.: Crescer com rentabilidade"
          onChange={(e) => setTitle(e.target.value)}
          className="h-11"
        />
      </FormField>
      <FormField id={ids.description} label="Explicação (opcional)" error={errors.description}>
        <textarea
          {...fieldA11y(ids.description, errors.description)}
          value={description}
          maxLength={1000}
          rows={3}
          onChange={(e) => setDescription(e.target.value)}
          className={textareaClass}
        />
      </FormField>
      {formError ? <FormAlert tone="error">{errorMessage(formError)}</FormAlert> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
        <Button type="submit" loading={saving}>
          {initial ? 'Salvar diretriz' : 'Adicionar diretriz'}
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------------------------
// Premissas

function PremisesSection({
  organizationId,
  projectId,
  can,
}: {
  organizationId: string;
  projectId: string;
  can: Can;
}) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const [category, setCategory] = useState<PremiseCategoryName | ''>('');
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState<Premise | 'new' | null>(null);
  const [busy, setBusy] = useState(false);
  const [archiving, setArchiving] = useState<Premise | null>(null);
  const filterId = useId();
  const base = `/organizations/${organizationId}/projects/${projectId}/premises`;
  const key = ['premises', projectId];

  const active = useQuery({
    queryKey: [...key, 'active', category],
    queryFn: ({ signal }) =>
      client.request(base, { query: { category: category || undefined }, schema: premiseListSchema, signal }),
    placeholderData: keepPreviousData,
  });
  const archivedQuery = useQuery({
    queryKey: [...key, 'archived'],
    queryFn: ({ signal }) =>
      client.request(base, { query: { archived: 'true' }, schema: premiseListSchema, signal }),
    enabled: showArchived,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: key });

  const toggleArchive = useMutation({
    meta: { handlesError: true },
    mutationFn: (p: Premise) =>
      client.request(`${base}/${p.id}/${p.archivedAt ? 'restore' : 'archive'}`, {
        method: 'POST',
        schema: premiseSchema,
      }),
    onSuccess: async (p) => {
      setArchiving(null);
      toast.success(p.archivedAt ? 'Premissa arquivada' : 'Premissa restaurada', { description: p.text });
      await refresh();
    },
    onError: (err) => {
      setArchiving(null);
      toast.error('Não foi possível concluir', { description: errorMessage(err) });
    },
  });

  async function save(input: PremiseInput) {
    const target = editing === 'new' ? null : editing;
    await client.request(target ? `${base}/${target.id}` : base, {
      method: target ? 'PUT' : 'POST',
      body: input,
      schema: premiseSchema,
    });
    setEditing(null);
    toast.success(target ? 'Premissa salva' : 'Premissa cadastrada');
    await refresh();
  }

  const items = active.data?.items ?? [];
  const now = localIsoDay();

  return (
    <section aria-label="Premissas">
      <SectionTitle icon={Lightbulb} title="Premissas">
        {can('planejamento-estrategico', 'create') ? (
          <Button size="sm" onClick={() => setEditing('new')}>
            <Plus aria-hidden />
            Nova premissa
          </Button>
        ) : null}
      </SectionTitle>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-prose text-sm text-muted-foreground">
          O que o plano assume como verdade (economia, mercado, operação…) e por quanto tempo.
        </p>
        <div className="w-full sm:w-56">
          <label htmlFor={filterId} className="sr-only">
            Filtrar premissas por categoria
          </label>
          <NativeSelect
            id={filterId}
            value={category}
            onChange={(e) => setCategory(e.target.value as PremiseCategoryName | '')}
          >
            <option value="">Todas as categorias</option>
            {PREMISE_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {PREMISE_CATEGORY_LABELS[c]}
              </option>
            ))}
          </NativeSelect>
        </div>
      </div>
      {active.isPending ? (
        <LoadingState rows={3} label="Carregando as premissas" />
      ) : active.isError ? (
        <ErrorState error={active.error} onRetry={() => void active.refetch()} retrying={active.isFetching} />
      ) : items.length === 0 ? (
        <EmptyState
          className="rounded-lg border border-dashed border-border py-8"
          icon={Lightbulb}
          title={category ? 'Nenhuma premissa nessa categoria' : 'Nenhuma premissa ainda'}
          description={
            category
              ? 'Escolha outra categoria ou veja todas.'
              : 'Registre o que o plano assume, como “inflação abaixo de 5% ao ano”.'
          }
          action={
            category ? (
              <Button variant="outline" onClick={() => setCategory('')}>
                Ver todas
              </Button>
            ) : null
          }
        />
      ) : (
        <ul aria-label="Premissas do projeto" aria-busy={active.isFetching} className="flex flex-col gap-2">
          {items.map((p) => {
            const inForce = premiseInForce(p, now);
            return (
              <li
                key={p.id}
                className="flex flex-col gap-3 rounded-lg border border-border bg-surface p-4 sm:flex-row sm:items-center"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-semibold">{p.text}</p>
                  <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <Badge variant="neutral">{PREMISE_CATEGORY_LABELS[p.category]}</Badge>
                    <span>{validityText(p)}</span>
                    <span
                      className={cn(
                        'font-medium',
                        inForce ? 'text-status-green-fg' : 'text-muted-foreground',
                      )}
                    >
                      {inForce ? 'Vigente hoje' : 'Fora de vigência hoje'}
                    </span>
                  </div>
                </div>
                <div className="flex justify-end gap-1">
                  {can('planejamento-estrategico', 'edit') ? (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Editar ${p.text}`}
                      onClick={() => setEditing(p)}
                    >
                      <Pencil aria-hidden />
                    </Button>
                  ) : null}
                  {can('planejamento-estrategico', 'delete') ? (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Arquivar ${p.text}`}
                      onClick={() => setArchiving(p)}
                    >
                      <Archive aria-hidden />
                    </Button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <ArchivedToggle
        checked={showArchived}
        onChange={setShowArchived}
        label="Mostrar premissas arquivadas"
      />
      {showArchived ? (
        <ArchivedList
          label="Premissas arquivadas"
          query={archivedQuery}
          empty="Nenhuma premissa arquivada."
          render={(p: Premise) => p.text}
          canRestore={can('planejamento-estrategico', 'delete')}
          restoring={toggleArchive.isPending}
          onRestore={(p) => toggleArchive.mutate(p)}
        />
      ) : null}

      <Dialog
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        busy={busy}
        title={editing === 'new' ? 'Nova premissa' : 'Editar premissa'}
      >
        {editing !== null ? (
          <PremiseForm
            initial={editing === 'new' ? undefined : editing}
            defaultCategory={category || undefined}
            onSubmit={save}
            onCancel={() => setEditing(null)}
            onBusyChange={setBusy}
          />
        ) : null}
      </Dialog>
      <ConfirmDialog
        open={archiving !== null}
        onOpenChange={(open) => !open && setArchiving(null)}
        title="Arquivar a premissa?"
        description="A premissa sai do plano ativo. O histórico fica guardado e você pode restaurá-la depois."
        confirmLabel="Arquivar premissa"
        destructive
        busy={toggleArchive.isPending}
        onConfirm={() => archiving && toggleArchive.mutate(archiving)}
      />
    </section>
  );
}

function validityText(p: Pick<Premise, 'validFrom' | 'validTo'>): string {
  if (p.validFrom && p.validTo) return `Vigência: ${formatDay(p.validFrom)} a ${formatDay(p.validTo)}`;
  if (p.validFrom) return `Vigência: a partir de ${formatDay(p.validFrom)}`;
  if (p.validTo) return `Vigência: até ${formatDay(p.validTo)}`;
  return 'Vigência: sem prazo';
}

function PremiseForm({
  initial,
  defaultCategory,
  onSubmit,
  onCancel,
  onBusyChange,
}: {
  initial?: Premise;
  defaultCategory?: PremiseCategoryName;
  onSubmit: (input: PremiseInput) => Promise<void>;
  onCancel: () => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const [text, setText] = useState(initial?.text ?? '');
  const [category, setCategory] = useState<PremiseCategoryName | ''>(
    initial?.category ?? defaultCategory ?? '',
  );
  const [validFrom, setValidFrom] = useState(initial?.validFrom ?? '');
  const [validTo, setValidTo] = useState(initial?.validTo ?? '');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const ids = { text: useId(), category: useId(), from: useId(), to: useId() };

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setFormError(null);
    const parsed = premiseInputSchema.safeParse({
      text,
      category: category || undefined,
      validFrom: validFrom || null,
      validTo: validTo || null,
    });
    if (!parsed.success) return setErrors(zodFieldErrors(parsed.error));
    setErrors({});
    setSaving(true);
    onBusyChange(true);
    try {
      await onSubmit(parsed.data);
    } catch (err) {
      const fields = apiFieldErrors(err);
      if (hasFieldErrors(fields)) setErrors(fields);
      else setFormError(err);
    } finally {
      setSaving(false);
      onBusyChange(false);
    }
  }

  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-5">
      <FormField id={ids.text} label="Premissa" error={errors.text}>
        <textarea
          {...fieldA11y(ids.text, errors.text)}
          value={text}
          maxLength={500}
          rows={2}
          placeholder="Ex.: Inflação abaixo de 5% ao ano"
          onChange={(e) => setText(e.target.value)}
          className={textareaClass}
        />
      </FormField>
      <FormField id={ids.category} label="Categoria" error={errors.category}>
        <NativeSelect
          {...fieldA11y(ids.category, errors.category)}
          value={category}
          onChange={(e) => setCategory(e.target.value as PremiseCategoryName | '')}
        >
          <option value="">Escolha a categoria</option>
          {PREMISE_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {PREMISE_CATEGORY_LABELS[c]}
            </option>
          ))}
        </NativeSelect>
      </FormField>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField id={ids.from} label="Vigente a partir de (opcional)" error={errors.validFrom}>
          <Input
            {...fieldA11y(ids.from, errors.validFrom)}
            type="date"
            value={validFrom}
            onChange={(e) => setValidFrom(e.target.value)}
            className="h-11"
          />
        </FormField>
        <FormField id={ids.to} label="Vigente até (opcional)" error={errors.validTo}>
          <Input
            {...fieldA11y(ids.to, errors.validTo)}
            type="date"
            value={validTo}
            onChange={(e) => setValidTo(e.target.value)}
            className="h-11"
          />
        </FormField>
      </div>
      {formError ? <FormAlert tone="error">{errorMessage(formError)}</FormAlert> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
        <Button type="submit" loading={saving}>
          {initial ? 'Salvar premissa' : 'Cadastrar premissa'}
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------------------------
// Peças comuns: arquivados

function ArchivedToggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
}) {
  return (
    <label className="mt-3 inline-flex items-center gap-2 text-sm text-muted-foreground">
      <input
        type="checkbox"
        className="size-4 accent-(--brand)"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  );
}

function ArchivedList<T extends { id: string }>({
  label,
  query,
  empty,
  render,
  canRestore,
  restoring,
  onRestore,
}: {
  label: string;
  query: {
    isPending: boolean;
    isError: boolean;
    error: unknown;
    isFetching: boolean;
    data?: { items: T[] };
    refetch: () => unknown;
  };
  empty: string;
  render: (item: T) => string;
  canRestore: boolean;
  restoring: boolean;
  onRestore: (item: T) => void;
}) {
  if (query.isPending) return <LoadingState className="mt-2" rows={2} label={`Carregando: ${label}`} />;
  if (query.isError) {
    return (
      <ErrorState
        className="mt-2"
        error={query.error}
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
      />
    );
  }
  const items = query.data?.items ?? [];
  if (items.length === 0) return <p className="mt-2 text-sm text-muted-foreground">{empty}</p>;
  return (
    <ul aria-label={label} className="mt-2 flex flex-col gap-2">
      {items.map((item) => (
        <li
          key={item.id}
          className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-border px-4 py-3"
        >
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-muted-foreground">{render(item)}</p>
            <p className="text-xs text-muted-foreground">Arquivada — o histórico continua guardado.</p>
          </div>
          {canRestore ? (
            <Button variant="outline" size="sm" disabled={restoring} onClick={() => onRestore(item)}>
              <ArchiveRestore aria-hidden />
              Restaurar
            </Button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------------------------
// Objetivos com pesos e relações (retrato; a edição fica em Perspectivas e Objetivos)

function ObjectivesOverview({ organizationId, projectId }: { organizationId: string; projectId: string }) {
  const { client } = useAuth();
  // Atalho para as metas (T-062, RF-14): só para quem vê o módulo de metas.
  const can = useCan();
  const canSeeTargets = can('metas-resultados', 'view');
  // E para os cenários (T-180b, RF-14): só para quem vê o módulo de cenários.
  const canSeeScenarios = can('cenarios', 'view');
  const project = `/organizations/${organizationId}/projects/${projectId}`;
  const perspectives = useQuery({
    queryKey: ['perspectives', projectId, false],
    queryFn: ({ signal }) =>
      client.request(`${project}/perspectives`, { schema: perspectiveListSchema, signal }),
  });
  const objectives = useQuery({
    queryKey: ['objectives', projectId, 'all-active'],
    queryFn: async ({ signal }) => {
      // Todos os objetivos ativos do projeto (página a página).
      const all: Objective[] = [];
      for (let page = 1, pages = 1; page <= pages; page++) {
        const res = await client.request(`${project}/objectives`, {
          query: { page: String(page), pageSize: '100' },
          schema: objectiveListSchema,
          signal,
        });
        all.push(...res.items);
        pages = res.totalPages;
      }
      return all;
    },
  });
  const workspacePath = (path: string) => `/empresas/${organizationId}/estrategia/${path}`;

  return (
    <section aria-label="Objetivos com pesos e relações">
      <SectionTitle icon={Target} title="Objetivos, pesos e relações">
        <div className="flex flex-wrap gap-2">
          <Button asChild size="sm" variant="outline">
            <Link to={workspacePath('perspectivas')}>Perspectivas e pesos</Link>
          </Button>
          <Button asChild size="sm" variant="outline">
            <Link to={workspacePath('objetivos')}>Gerenciar objetivos</Link>
          </Button>
          {canSeeTargets ? (
            <Button asChild size="sm" variant="outline">
              <Link to={`/empresas/${organizationId}/indicadores/metas`}>Metas anuais e mensais</Link>
            </Button>
          ) : null}
          {canSeeScenarios ? (
            <Button asChild size="sm" variant="outline">
              <Link to={`/empresas/${organizationId}/planejamento/cenarios?projeto=${projectId}`}>
                Cenários
              </Link>
            </Button>
          ) : null}
        </div>
      </SectionTitle>
      {perspectives.isPending || objectives.isPending ? (
        <LoadingState rows={3} label="Carregando os objetivos" />
      ) : perspectives.isError || objectives.isError ? (
        <ErrorState
          error={perspectives.error ?? objectives.error}
          onRetry={() => {
            void perspectives.refetch();
            void objectives.refetch();
          }}
          retrying={perspectives.isFetching || objectives.isFetching}
        />
      ) : (
        <ObjectivesByPerspective
          perspectives={perspectives.data.items}
          objectives={objectives.data}
          total={perspectiveWeightTotal(perspectives.data.items)}
        />
      )}
    </section>
  );
}

function ObjectivesByPerspective({
  perspectives,
  objectives,
  total,
}: {
  perspectives: { id: string; name: string; weight: number | null }[];
  objectives: Objective[];
  total: number;
}) {
  if (objectives.length === 0) {
    return (
      <EmptyState
        className="rounded-lg border border-dashed border-border py-8"
        icon={Target}
        title="Nenhum objetivo ainda"
        description="Cadastre os objetivos em Gerenciar objetivos; eles aparecem aqui com pesos e relações."
      />
    );
  }
  return (
    <div className="flex flex-col gap-4">
      <p className={cn('text-sm', total === 100 ? 'text-status-green-fg' : 'text-status-yellow-fg')}>
        Pesos das perspectivas somam {formatWeight(total)}
        {total === 100 ? '.' : ' — o ideal é somar 100%.'}
      </p>
      {perspectives.map((p, i) => {
        const list = objectives.filter((o) => o.perspective.id === p.id);
        const objectiveTotal = Math.round(list.reduce((s, o) => s + (o.weight ?? 0), 0) * 100) / 100;
        const weighted = list.some((o) => o.weight !== null);
        return (
          <div key={p.id} className="overflow-hidden rounded-lg border border-border bg-surface">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border px-4 py-3">
              <span className={cn('size-2.5 shrink-0 rounded-full', SERIES[i % SERIES.length])} aria-hidden />
              <h3 className="font-bold">{p.name}</h3>
              <span className="text-sm text-muted-foreground">
                {p.weight === null ? 'sem peso' : `peso ${formatWeight(p.weight)}`} · {list.length}{' '}
                {list.length === 1 ? 'objetivo' : 'objetivos'}
              </span>
              {weighted && objectiveTotal !== 100 ? (
                <span className="text-xs font-medium text-status-yellow-fg">
                  pesos dos objetivos somam {formatWeight(objectiveTotal)}
                </span>
              ) : null}
            </div>
            {list.length === 0 ? (
              <p className="px-4 py-3 text-sm text-muted-foreground">Nenhum objetivo nesta perspectiva.</p>
            ) : (
              <ul aria-label={`Objetivos de ${p.name} no planejamento`} className="divide-y divide-border">
                {list.map((o) => (
                  <li
                    key={o.id}
                    className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-start sm:gap-4"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium">{o.name}</p>
                      {o.relations.length ? (
                        <ul className="mt-1 flex flex-col gap-0.5 text-xs text-muted-foreground">
                          {o.relations.map((r) => (
                            <li key={`${r.type}:${r.objective.id}`}>
                              {OBJECTIVE_RELATION_LABELS[r.type]} → {r.objective.name}
                              {r.objective.archived ? ' (arquivado)' : ''}
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                    <span className="text-sm tabular-nums text-muted-foreground">
                      {o.weight === null ? 'sem peso' : formatWeight(o.weight)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}
